'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { buildQuerySummary, buildResultsPerPage } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { DEFAULT_SEARCH_PAGE_SIZE } from '@lib/constants/coveo';
import { useFlags } from '@lib/flags/amplitude/client';
import { Flags } from '@lib/flags/flagConstants';
import { useController } from '@lib/hooks/useController';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { Check, X } from 'phosphor-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { c } from 'ui-components';
import { CategoryFacet } from '../CategoryFacet';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { OwnershipFacet } from '../OwnershipFacet';
import { PriceFacet } from '../PriceFacet';
import { PublisherFacet } from '../PublisherFacet';
import { RatingFacet } from '../RatingFacet';
import { ReleaseDateFacet } from '../ReleaseDateFacet';
import { SaleFacet } from '../SaleFacet';
import {
  getCategoryController,
  getPriceController,
  getPublisherController,
  getRatingController,
  getReleaseDateController,
  getSaleController,
} from '../facetControllers';
import { useSearchV2Sort } from '../hooks/useSearchV2Sort';
import { clearOwnershipFilter } from '../utils/clearOwnershipFilter';
import {
  getResultsPerPageFromLocalStorage,
  setResultsPerPageToLocalStorage,
} from '../utils/resultsPerPageStorage';
import { MobileFilterDrawerProps } from './MobileFilterDrawer.types';

export const MobileFilterDrawer = ({
  isOpen,
  onClose,
}: MobileFilterDrawerProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();
  const { flags } = useFlags();
  const flag = flags[Flags.INCREASE_DEFAULT_RESULTS_PER_PAGE];
  const defaultPageSize = flag?.payload?.pageSize || DEFAULT_SEARCH_PAGE_SIZE;
  const session = useSession();
  const searchParams = useSearchParams();

  const getLabel = (key: string, fallback: string) =>
    searchLabelsMicrocopy?.find((m) => m.key === key)?.value ?? fallback;

  // ── Accordion open/close state ──
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((section: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const eng = useEngine();
  const { currentSortValue, sortCriteria, handleSort } = useSearchV2Sort();

  // ── Facet controllers (subscribed for counting only) ──
  const priceController = useMemo(() => getPriceController(eng), [eng]);
  const priceState = useController(priceController);
  const ratingController = useMemo(() => getRatingController(eng), [eng]);
  const ratingState = useController(ratingController);
  const saleController = useMemo(() => getSaleController(eng), [eng]);
  const saleState = useController(saleController);
  const publisherController = useMemo(() => getPublisherController(eng), [eng]);
  const publisherState = useController(publisherController);
  const dateController = useMemo(() => getReleaseDateController(eng), [eng]);
  const dateState = useController(dateController);
  const categoryController = useMemo(() => getCategoryController(eng), [eng]);
  const categoryState = useController(categoryController);

  // ── Selection counts ──
  const countSelected = (values: Array<{ state: string }>) =>
    values.filter((v) => v.state === 'selected').length;

  const ownershipChecked = searchParams?.get('hideOwnership') === 'true';

  const totalSelectedCount = useMemo(
    () =>
      countSelected(priceState.values) +
      countSelected(ratingState.values) +
      countSelected(saleState.values) +
      countSelected(publisherState.values) +
      countSelected(dateState.values) +
      (categoryState.values?.filter(
        (v: { state: string }) => v.state === 'selected'
      ).length ?? 0) +
      (ownershipChecked ? 1 : 0),
    [
      priceState.values,
      ratingState.values,
      saleState.values,
      publisherState.values,
      dateState.values,
      categoryState.values,
      ownershipChecked,
    ]
  );

  // ── Results per page ──
  const [initialNumberOfResults] = useState(
    () => getResultsPerPageFromLocalStorage() ?? defaultPageSize
  );

  const resultsPerPageController = useMemo(
    () =>
      buildResultsPerPage(eng, {
        initialState: { numberOfResults: initialNumberOfResults },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const resultsPerPageState = useController(resultsPerPageController);

  // ── Query summary (total results) ──
  const querySummaryController = useMemo(() => buildQuerySummary(eng), [eng]);
  const querySummaryState = useController(querySummaryController);

  // ── Body scroll lock ──
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // ── Clear all filters ──
  const handleClearAll = useCallback(() => {
    priceController.deselectAll();
    ratingController.deselectAll();
    saleController.deselectAll();
    publisherController.deselectAll();
    categoryController.deselectAll();
    dateController.deselectAll();
    clearOwnershipFilter();
  }, [
    priceController,
    ratingController,
    saleController,
    publisherController,
    categoryController,
    dateController,
  ]);

  // ── Results per page handler ──
  const handleResultsPerPage = useCallback(
    (size: number) => {
      resultsPerPageController.set(size);
      setResultsPerPageToLocalStorage(size);
    },
    [resultsPerPageController]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={getLabel('search.mobile.sort-and-filter', 'Sort and filter')}
    >
      {/* Header */}
      <div className="flex h-14 shrink-0 items-center justify-end px-4">
        <button
          type="button"
          onClick={onClose}
          aria-label={getLabel('search.filters.close', 'Close')}
          className="text-grey-400 hover:text-white"
        >
          <X size={24} />
        </button>
      </div>

      {/* Scrollable facets */}
      <div className="flex-1 overflow-y-auto">
        <CategoryFacet
          variant="mobile"
          isOpen={openSections.has('categories')}
          onToggle={() => toggleSection('categories')}
        />
        <PriceFacet
          variant="mobile"
          isOpen={openSections.has('price')}
          onToggle={() => toggleSection('price')}
        />
        <SaleFacet
          variant="mobile"
          isOpen={openSections.has('sale')}
          onToggle={() => toggleSection('sale')}
        />
        <RatingFacet
          variant="mobile"
          isOpen={openSections.has('rating')}
          onToggle={() => toggleSection('rating')}
        />
        <PublisherFacet
          variant="mobile"
          isOpen={openSections.has('publisher')}
          onToggle={() => toggleSection('publisher')}
        />
        <ReleaseDateFacet
          variant="mobile"
          isOpen={openSections.has('date')}
          onToggle={() => toggleSection('date')}
        />
        {session.status === 'authenticated' && (
          <OwnershipFacet
            variant="mobile"
            isOpen={openSections.has('ownership')}
            onToggle={() => toggleSection('ownership')}
          />
        )}

        {/* Sort By */}
        <FacetAccordion
          title={getLabel('search.sort-by', 'Sort by')}
          variant="mobile"
          isOpen={openSections.has('sort')}
          onToggle={() => toggleSection('sort')}
        >
          <div className="flex flex-col">
            {sortCriteria.map((opt) => {
              const key = `search.${opt.value}`;
              const sortLabel =
                searchLabelsMicrocopy?.find((m) => m.key === key)?.value ??
                opt.value.charAt(0).toUpperCase() + opt.value.slice(1);
              const isSelected = currentSortValue === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSort(opt.value)}
                  className={c(
                    'flex items-center justify-between rounded-lg px-1 py-3 text-sm',
                    isSelected
                      ? 'font-medium text-white'
                      : 'text-grey-300 hover:text-white'
                  )}
                >
                  {sortLabel}
                  {isSelected && (
                    <Check size={16} className="shrink-0 text-white" />
                  )}
                </button>
              );
            })}
          </div>
        </FacetAccordion>

        {/* Results per page */}
        <FacetAccordion
          title={getLabel('search.results-per-page', 'Results')}
          variant="mobile"
          isOpen={openSections.has('results')}
          onToggle={() => toggleSection('results')}
        >
          <div className="flex flex-col">
            {[24, 48, 96].map((size) => {
              const isSelected = resultsPerPageState.numberOfResults === size;
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => handleResultsPerPage(size)}
                  className={c(
                    'flex items-center justify-between rounded-lg px-1 py-3 text-sm',
                    isSelected
                      ? 'font-medium text-white'
                      : 'text-grey-300 hover:text-white'
                  )}
                >
                  {size}
                  {isSelected && (
                    <Check size={16} className="shrink-0 text-white" />
                  )}
                </button>
              );
            })}
          </div>
        </FacetAccordion>
      </div>

      {/* Bottom sticky bar */}
      <div className="shrink-0 border-t border-grey-800 px-4 pb-6 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="flex w-full items-center justify-center rounded-full border-2 border-blue py-3 text-sm font-semibold uppercase tracking-wider text-blue hover:bg-blue/10"
        >
          {getLabel('search.mobile.view-results', 'View results')} (
          {querySummaryState.total.toLocaleString()})
        </button>
        <button
          type="button"
          onClick={handleClearAll}
          className="mt-3 w-full text-center text-sm text-grey-400 hover:text-grey-200"
        >
          {getLabel('search.mobile.clear', 'Clear')}
          {totalSelectedCount > 0 && ` (${totalSelectedCount})`}
        </button>
      </div>
    </div>
  );
};
